import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Typography, Card, CardContent, TextField,
  ToggleButton, ToggleButtonGroup, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  Chip, LinearProgress, Divider, IconButton, Tooltip
} from '@mui/material';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip as RTooltip, Legend, AreaChart, Area,
  BarChart
} from 'recharts';
import {
  SwapHorizOutlined, ArrowUpwardOutlined, ArrowDownwardOutlined,
  PeopleOutlined, NotificationsOutlined, SchoolOutlined,
  MonitorHeartOutlined, BloodtypeOutlined, RefreshOutlined
} from '@mui/icons-material';
import API from '../utils/api';

const TT  = { backgroundColor:'#111827', border:'1px solid rgba(0,191,165,0.2)', borderRadius:8, fontFamily:'IBM Plex Mono' };
const MTN = '#FFB300';
const EM  = '#00BFA5';
const OTH = '#90A4AE';

const MSG_TYPES = [
  { key:'enrol',    label:'Enrolment',                  color:'#40C4FF',  icon:<PeopleOutlined fontSize="small"/> },
  { key:'reminder', label:'Appointment Reminder',       color:'#FFB300',  icon:<NotificationsOutlined fontSize="small"/> },
  { key:'hypert',   label:'Hypertension Educational',   color:'#FF5252',  icon:<MonitorHeartOutlined fontSize="small"/> },
  { key:'diab',     label:'Diabetes Educational',       color:'#CE93D8',  icon:<BloodtypeOutlined fontSize="small"/> },
];

const TELECOMS = [
  { key:'MTN',              color: MTN },
  { key:'Eswatini_Mobile',  color: EM  },
  { key:'Other',            color: OTH },
];

function KpiBox({ label, out, inn, color, icon }) {
  const total = (out||0) + (inn||0);
  return (
    <Card sx={{ height:'100%' }}>
      <CardContent sx={{ p:2 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Box sx={{ color }}>{icon}</Box>
          <Typography variant="caption" color="text.secondary"
            sx={{ fontFamily:'"IBM Plex Mono",monospace', fontSize:'0.68rem',
                  textTransform:'uppercase', letterSpacing:'0.07em' }}>
            {label}
          </Typography>
        </Box>
        <Typography variant="h5" fontFamily='"IBM Plex Mono",monospace' fontWeight={700} color={color}>
          {total.toLocaleString()}
        </Typography>
        <Box display="flex" gap={2} mt={0.5}>
          <Box display="flex" alignItems="center" gap={0.3}>
            <ArrowUpwardOutlined sx={{ fontSize:13, color:'#69F0AE' }} />
            <Typography variant="caption" color="#69F0AE" fontFamily='"IBM Plex Mono",monospace'>
              {(out||0).toLocaleString()} out
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.3}>
            <ArrowDownwardOutlined sx={{ fontSize:13, color:'#40C4FF' }} />
            <Typography variant="caption" color="#40C4FF" fontFamily='"IBM Plex Mono",monospace'>
              {(inn||0).toLocaleString()} in
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function TelecomTrafficCard({ telecom, color, data }) {
  const total = (data?.totalOut||0) + (data?.totalIn||0);
  const rows = [
    { label:'Enrolment',              out: data?.enrolOut,    inn: data?.enrolIn },
    { label:'Appt Reminder',          out: data?.reminderOut, inn: data?.reminderIn },
    { label:'Hypertension Education', out: data?.hypertOut,   inn: data?.hypertIn },
    { label:'Diabetes Education',     out: data?.diabOut,     inn: data?.diabIn },
  ];
  return (
    <Card sx={{ height:'100%', borderColor:`${color}30` }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h6" fontWeight={700} color={color}>{telecom.replace('_',' ')}</Typography>
            <Typography variant="caption" color="text.secondary" fontFamily='"IBM Plex Mono",monospace'>
              {total.toLocaleString()} total messages
            </Typography>
          </Box>
          <Box textAlign="right">
            <Box display="flex" alignItems="center" gap={0.5}>
              <ArrowUpwardOutlined sx={{ fontSize:14, color:'#69F0AE' }} />
              <Typography variant="caption" color="#69F0AE" fontFamily='"IBM Plex Mono",monospace'>{(data?.totalOut||0).toLocaleString()}</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <ArrowDownwardOutlined sx={{ fontSize:14, color:'#40C4FF' }} />
              <Typography variant="caption" color="#40C4FF" fontFamily='"IBM Plex Mono",monospace'>{(data?.totalIn||0).toLocaleString()}</Typography>
            </Box>
          </Box>
        </Box>

        {rows.map((row, i) => {
          const rowTotal = (row.out||0) + (row.inn||0);
          const pct = total > 0 ? (rowTotal / total) * 100 : 0;
          return (
            <Box key={i} mb={1.5}>
              <Box display="flex" justifyContent="space-between" mb={0.3}>
                <Typography variant="caption" color="text.secondary" fontSize="0.7rem">{row.label}</Typography>
                <Typography variant="caption" fontFamily='"IBM Plex Mono",monospace' fontSize="0.7rem" color={color}>
                  ↑{(row.out||0)} ↓{(row.inn||0)}
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={Math.min(pct,100)}
                sx={{ height:5, borderRadius:3, bgcolor:'rgba(255,255,255,0.06)',
                  '& .MuiLinearProgress-bar':{ bgcolor:color, borderRadius:3 } }} />
            </Box>
          );
        })}
      </CardContent>
    </Card>
  );
}

export default function TrafficPage() {
  const [data,     setData]     = useState({ monthly:[], summary:[], breakdown:[] });
  const [dateFrom, setDateFrom] = useState('2025-02-01');
  const [dateTo,   setDateTo]   = useState(new Date().toISOString().split('T')[0]);
  const [view,     setView]     = useState('total');   // total | enrol | reminder | hypert | diab
  const [split,    setSplit]    = useState('combined'); // combined | mtn | em
  const [loading,  setLoading]  = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get('/dashboard/traffic/monthly', {
        params: { dateFrom, dateTo }
      });
      setData(res.data);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  // Build chart data based on current view + split
  const chartData = data.monthly.map(row => {
    const mtn = 'MTN';
    const em  = 'Eswatini_Mobile';
    const typeKey = view === 'total' ? 'total' : view;

    if (split === 'combined') {
      const out = (row[`${mtn}_${typeKey}_out`]||0) + (row[`${em}_${typeKey}_out`]||0);
      const inn = (row[`${mtn}_${typeKey}_in`]||0)  + (row[`${em}_${typeKey}_in`]||0);
      return { month: row.month, 'Outbound': out, 'Inbound': inn };
    }
    const tc = split === 'mtn' ? mtn : em;
    return {
      month:    row.month,
      Outbound: row[`${tc}_${typeKey}_out`] || 0,
      Inbound:  row[`${tc}_${typeKey}_in`]  || 0,
    };
  });

  // Summary cards — aggregate across selected telecoms
  const getSummary = (telecomKey) =>
    data.summary.find(s => s.telecom === telecomKey.replace('_',' ')) || {};

  const mtnS = getSummary('MTN');
  const emS  = getSummary('Eswatini_Mobile');

  // Combined totals for KPI row
  const combined = data.summary.reduce((acc, s) => ({
    enrolOut:    acc.enrolOut    + (s.enrolOut    || 0),
    enrolIn:     acc.enrolIn     + (s.enrolIn     || 0),
    reminderOut: acc.reminderOut + (s.reminderOut || 0),
    reminderIn:  acc.reminderIn  + (s.reminderIn  || 0),
    hypertOut:   acc.hypertOut   + (s.hypertOut   || 0),
    hypertIn:    acc.hypertIn    + (s.hypertIn    || 0),
    diabOut:     acc.diabOut     + (s.diabOut     || 0),
    diabIn:      acc.diabIn      + (s.diabIn      || 0),
  }), { enrolOut:0, enrolIn:0, reminderOut:0, reminderIn:0, hypertOut:0, hypertIn:0, diabOut:0, diabIn:0 });

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="primary.main">
            <SwapHorizOutlined sx={{ mr:1, verticalAlign:'middle' }} />
            Message Traffic Flow
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monthly in/out per telecom · by message type
          </Typography>
        </Box>

        {/* Date range */}
        <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
          <TextField size="small" type="date" label="From"
            value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink:true }} sx={{ width:155 }} />
          <TextField size="small" type="date" label="To"
            value={dateTo} onChange={e => setDateTo(e.target.value)}
            InputLabelProps={{ shrink:true }} sx={{ width:155 }} />
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={load} sx={{ color:'primary.main' }}>
              <RefreshOutlined />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb:2 }} />}

      {/* KPI row — all types combined */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={6} sm={3}>
          <KpiBox label="Enrolment"             out={combined.enrolOut}    inn={combined.enrolIn}    color="#40C4FF" icon={<PeopleOutlined fontSize="small"/>} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiBox label="Appt Reminders"        out={combined.reminderOut} inn={combined.reminderIn} color="#FFB300" icon={<NotificationsOutlined fontSize="small"/>} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiBox label="Hypertension Edu"      out={combined.hypertOut}   inn={combined.hypertIn}   color="#FF5252" icon={<MonitorHeartOutlined fontSize="small"/>} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiBox label="Diabetes Edu"          out={combined.diabOut}     inn={combined.diabIn}     color="#CE93D8" icon={<BloodtypeOutlined fontSize="small"/>} />
        </Grid>
      </Grid>

      {/* Per-telecom breakdown cards */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={6}>
          <TelecomTrafficCard telecom="MTN"            color={MTN} data={mtnS} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TelecomTrafficCard telecom="Eswatini_Mobile" color={EM} data={emS}  />
        </Grid>
      </Grid>

      {/* Chart controls */}
      <Card sx={{ mb:2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1.5}>
            <Typography variant="h6">Monthly Traffic Chart</Typography>
            <Box display="flex" gap={1.5} flexWrap="wrap">
              {/* Message type selector */}
              <ToggleButtonGroup value={view} exclusive onChange={(_,v) => v && setView(v)} size="small"
                sx={{ '& .MuiToggleButton-root':{ fontFamily:'"IBM Plex Mono",monospace', fontSize:'0.68rem', px:1.5, py:0.5 } }}>
                <ToggleButton value="total">   All</ToggleButton>
                <ToggleButton value="enrol">   Enrolment</ToggleButton>
                <ToggleButton value="reminder">Reminders</ToggleButton>
                <ToggleButton value="hypert">  Hypert.</ToggleButton>
                <ToggleButton value="diab">    Diabetes</ToggleButton>
              </ToggleButtonGroup>

              {/* Telecom split */}
              <ToggleButtonGroup value={split} exclusive onChange={(_,v) => v && setSplit(v)} size="small"
                sx={{ '& .MuiToggleButton-root':{ fontFamily:'"IBM Plex Mono",monospace', fontSize:'0.68rem', px:1.5, py:0.5 } }}>
                <ToggleButton value="combined">Both</ToggleButton>
                <ToggleButton value="mtn" sx={{ color: MTN }}>MTN</ToggleButton>
                <ToggleButton value="em"  sx={{ color: EM  }}>EM</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={chartData}>
              <defs>
                <linearGradient id="outGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#69F0AE" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#69F0AE" stopOpacity={0}   />
                </linearGradient>
                <linearGradient id="inGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#40C4FF" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#40C4FF" stopOpacity={0}   />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="month" tick={{ fontSize:11, fill:'#90A4AE', fontFamily:'IBM Plex Mono' }} />
              <YAxis tick={{ fontSize:11, fill:'#90A4AE' }} />
              <RTooltip contentStyle={TT} />
              <Legend />
              <Bar  dataKey="Outbound" fill="#69F0AE" radius={[4,4,0,0]} opacity={0.85} />
              <Bar  dataKey="Inbound"  fill="#40C4FF" radius={[4,4,0,0]} opacity={0.85} />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Full monthly breakdown table */}
      <Card>
        <CardContent>
          <Typography variant="h6" mb={2}>Monthly Detail Table</Typography>
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell rowSpan={2}>Month</TableCell>
                  <TableCell rowSpan={2}>Telecom</TableCell>
                  <TableCell align="center" colSpan={2} sx={{ color:'#40C4FF !important' }}>Enrolment</TableCell>
                  <TableCell align="center" colSpan={2} sx={{ color:'#FFB300 !important' }}>Appt Reminder</TableCell>
                  <TableCell align="center" colSpan={2} sx={{ color:'#FF5252 !important' }}>Hypertension Edu</TableCell>
                  <TableCell align="center" colSpan={2} sx={{ color:'#CE93D8 !important' }}>Diabetes Edu</TableCell>
                  <TableCell align="center" colSpan={2} sx={{ color:'#00BFA5 !important' }}>Total</TableCell>
                </TableRow>
                <TableRow>
                  {['↑Out','↓In','↑Out','↓In','↑Out','↓In','↑Out','↓In','↑Out','↓In'].map((h,i) => (
                    <TableCell key={i} align="right"
                      sx={{ fontSize:'0.65rem', fontFamily:'"IBM Plex Mono",monospace',
                        color: h.startsWith('↑') ? '#69F0AE' : '#40C4FF' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.monthly.map((row, idx) => (
                  TELECOMS.map((tc, ti) => {
                    const k = tc.key;
                    const isFirst = ti === 0;
                    return (
                      <TableRow key={`${idx}-${ti}`} hover
                        sx={{ '&:hover':{ bgcolor:'rgba(0,191,165,0.04)' },
                          borderTop: isFirst ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                        {isFirst && (
                          <TableCell rowSpan={3}
                            sx={{ fontFamily:'"IBM Plex Mono",monospace', fontWeight:600,
                              fontSize:'0.78rem', verticalAlign:'middle' }}>
                            {row.month}
                          </TableCell>
                        )}
                        <TableCell>
                          <Chip label={tc.key.replace('_',' ')} size="small"
                            sx={{ fontFamily:'"IBM Plex Mono",monospace', fontSize:'0.62rem',
                              height:18, bgcolor:`${tc.color}18`, color:tc.color }} />
                        </TableCell>
                        {[
                          row[`${k}_enrol_out`]||0,    row[`${k}_enrol_in`]||0,
                          row[`${k}_reminder_out`]||0, row[`${k}_reminder_in`]||0,
                          row[`${k}_hypert_out`]||0,   row[`${k}_hypert_in`]||0,
                          row[`${k}_diab_out`]||0,     row[`${k}_diab_in`]||0,
                          row[`${k}_total_out`]||0,    row[`${k}_total_in`]||0,
                        ].map((val, vi) => (
                          <TableCell key={vi} align="right"
                            sx={{ fontFamily:'"IBM Plex Mono",monospace', fontSize:'0.73rem',
                              color: vi%2===0 ? '#69F0AE' : '#40C4FF',
                              fontWeight: vi >= 8 ? 700 : 400 }}>
                            {val > 0 ? val : '—'}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}
